// Allocation & headroom editor (BP8 verb #4) — adjust pools + overage-allowance %.
//
// The install enforces a hard wall of pool × (1 + overage_allowance_pct). Raising
// the allowance gives a customer headroom without selling a pack; raising a pool
// grows base capacity (and billed seat MRR). PATCHes /api/v1/licenses/:id/allocation.
// KISS: shows the resulting wall inline so a billing person sees the effect before
// saving ("12 + 10% = 13 hard wall").
import React, { useState } from 'react';
import { apiRequest } from '@/lib/supabase';
import { useToast } from '@/components/ui/use-toast';
import { SlidersHorizontal } from 'lucide-react';

interface Props {
  licenseId: string;
  vendorPool: number | null;
  orgPool: number | null;
  overageAllowancePct: number; // fraction, 0.10 = 10%
  onSaved?: () => void;
  readOnly?: boolean;
}

export function AllocationCard({ licenseId, vendorPool, orgPool, overageAllowancePct, onSaved, readOnly = false }: Props) {
  const { toast } = useToast();
  const [vp, setVp] = useState(vendorPool != null ? String(vendorPool) : '');
  const [op, setOp] = useState(orgPool != null ? String(orgPool) : '');
  // Store the allowance as a whole-percent for the human; convert to fraction on save.
  const [pctStr, setPctStr] = useState(String(Math.round((overageAllowancePct || 0) * 100)));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const vpNum = parseInt(vp, 10);
  const pctNum = parseFloat(pctStr);
  const wall =
    Number.isFinite(vpNum) && vpNum > 0 && Number.isFinite(pctNum)
      ? Math.floor(vpNum * (1 + pctNum / 100))
      : null;

  const save = async () => {
    if (readOnly || busy) return;
    setError(null);
    const body: Record<string, unknown> = {};
    if (vp.trim() !== '') {
      const n = parseInt(vp, 10);
      if (!Number.isInteger(n) || n < 0) { setError('Vendor pool must be a whole number ≥ 0.'); return; }
      body.vendorPool = n;
    }
    if (op.trim() !== '') {
      const n = parseInt(op, 10);
      if (!Number.isInteger(n) || n < 0) { setError('Org pool must be a whole number ≥ 0.'); return; }
      body.orgPool = n;
    }
    if (pctStr.trim() !== '') {
      const p = parseFloat(pctStr);
      if (!Number.isFinite(p) || p < 0 || p > 500) { setError('Allowance must be 0–500%.'); return; }
      body.overageAllowancePct = p / 100;
    }
    if (Object.keys(body).length === 0) { setError('Nothing to change.'); return; }

    setBusy(true);
    try {
      await apiRequest(`/licenses/${licenseId}/allocation`, { method: 'PATCH', body: JSON.stringify(body) });
      toast({ title: 'Allocation updated — effective on the next phone-home.' });
      onSaved?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update allocation.');
    } finally {
      setBusy(false);
    }
  };

  const field: React.CSSProperties = { width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 13, outline: 'none', boxSizing: 'border-box' };
  const label: React.CSSProperties = { display: 'block', fontSize: 11.5, fontWeight: 500, color: 'var(--text-secondary)', marginBottom: 5 };

  return (
    <div className="card" style={{ marginTop: 16 }}>
      <div className="card-header">
        <span className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <SlidersHorizontal size={15} /> Allocation &amp; Headroom
        </span>
      </div>
      <div className="card-body">
        <fieldset disabled={readOnly || busy} style={{ border: 0, padding: 0, margin: 0, display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
          <div>
            <label style={label}>Vendor pool</label>
            <input type="number" min={0} value={vp} onChange={(e) => setVp(e.target.value)} placeholder="unlimited" style={field} />
          </div>
          <div>
            <label style={label}>Org pool</label>
            <input type="number" min={0} value={op} onChange={(e) => setOp(e.target.value)} placeholder="n/a" style={field} />
          </div>
          <div>
            <label style={label}>Overage allowance %</label>
            <input type="number" min={0} max={500} value={pctStr} onChange={(e) => setPctStr(e.target.value)} style={field} />
          </div>
        </fieldset>

        {wall != null && (
          <div style={{ marginTop: 10, fontSize: 12, color: 'var(--text-secondary)' }}>
            Hard wall: <strong>{vpNum.toLocaleString()}</strong> vendors + {Math.round(pctNum)}% ={' '}
            <strong style={{ color: 'var(--accent)' }}>{wall.toLocaleString()}</strong> before block.
          </div>
        )}

        {error && <div style={{ marginTop: 8, fontSize: 12, color: '#DC2626' }}>{error}</div>}

        {!readOnly && <div style={{ marginTop: 12 }}>
          <button
            onClick={save}
            disabled={busy}
            style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: busy ? 'var(--accent-muted)' : 'var(--accent)', color: 'white', cursor: busy ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 600 }}
          >
            {busy ? 'Saving…' : 'Save allocation'}
          </button>
        </div>}
      </div>
    </div>
  );
}
